import type { MutableState } from 'src/replicant/State';
import { convertUTCToClientTime } from '../getters/time';
import { duration, getPartOfTime } from '../utils/duration';
import {
  getMatch2Level,
  getMatch2TimedRewardRemainingTime,
} from './match2.getters';
import * as Inventory from '../inventory';
import { assertNever } from '../utils';
import { updateRaceScore } from './races/races.modifiers';
import { hasValidSaveState } from './savestate/match2-savestate.getters';
import {
  FeatureReward,
  Match2Reward,
  Match2TimedRewardType,
  isTimedReward,
  isUnprocessedReward,
} from './rewards/match2-rewards.ruleset';
import {
  getMatch2TimedRewardTimestampKey,
  getMatch2TimedRewardType,
} from './rewards/match2-rewards.getters';
import { addDecorationItem } from '../decoration/decoration.modifiers';
import {
  DecorationThemeID,
  decorationSlots,
  tiersByTheme,
} from '../decoration/decoration.ruleset';
import { DecorationThemeVariant } from '../data/DecorationItems.schema';
import { CurrencyGrantFeature } from '../analytics/events';
import { CurrencyGrantOpts } from '../inventory/inventory.utils';
import { SyncActionAPI } from '../utils/types';
import { grantCardPackByTier } from '../cardpack/cardpack.modifiers';
import { cardsAddCard } from '../cards/cards.modifiers';
import { sendAnalyticsEvents } from '../analytics';
import {
  activateSuperLollipop,
  clearSuperLollipopWinStreak,
} from '../superLollipop/superLollipop.modifiers';
import { isSuperLollipopActive } from '../superLollipop/superLollipop.getters';
import { boosterNames, powerupNames } from './match2.ruleset';
import {
  getCurrentLeaguePhase,
  hasJoinedLeague,
} from './leagues/leagues.getters';

/**
 * Reset daily bonus daily stats on a new day
 */
export function resetDailyBonusDailyStats(
  state: MutableState,
  now: number,
): void {
  const { match2 } = state;
  const clientSideTime = convertUTCToClientTime(now, state);
  const days = getPartOfTime(clientSideTime, 'days', true);

  if (!match2.dailyBonus.nextBonusTimestamp) {
    match2.dailyBonus.nextBonusTimestamp = now;
  }
  if (match2.dailyBonus.currentDayStamp === days) return;
  const dailyCollected = match2.dailyBonus.dailyRewardsCollected as Record<
    string,
    any
  >;

  for (const key in dailyCollected) {
    if (key === 'boosters' || key === 'powerups') {
      for (const subKey in dailyCollected[key]) {
        dailyCollected[key][subKey] = 0;
      }
    } else {
      dailyCollected[key] = 0;
    }
  }

  match2.dailyBonus.dailyCount = 0;
  match2.dailyBonus.currentDayStamp = days;
}

export function grantMatch2Rewards(
  state: MutableState,
  rewards: Match2Reward[],
  feature: CurrencyGrantFeature,
  api: SyncActionAPI,
  opts?: CurrencyGrantOpts,
): void {
  const now = api.date.now();
  const { boosters, powerups, inventory } = state.match2;

  for (let index = 0; index < rewards.length; index++) {
    const reward = rewards[index];

    if (isUnprocessedReward(reward)) {
      // Nested and random rewards should be processed beforehand
      continue;
    }

    if (isTimedReward(reward)) {
      const timedType = getMatch2TimedRewardType(reward)!;
      grantMatch2TimedReward(
        state,
        now,
        timedType,
        reward.duration,
        api,
        feature,
        opts,
      );
    } else {
      switch (reward.type) {
        case 'bPoints':
          Inventory.modifiers.addPoints(
            state,
            reward.amount ?? 0,
            feature,
            api,
            opts,
          );
          break;
        case 'gems':
          Inventory.modifiers.addFreeGems(
            state,
            reward.amount,
            feature,
            api,
            opts,
          );
          break;
        case 'energy':
          Inventory.modifiers.addEnergy(
            state,
            reward.amount,
            now,
            feature,
            api,
            opts,
          );
          break;
        case 'boosters':
          if (!boosters[reward.subType]) boosters[reward.subType] = 0;
          boosters[reward.subType] += reward.amount;
          sendAnalyticsEvents(api, [
            {
              eventType: 'BoosterGrant',
              eventProperties: {
                paidBoostersGranted: opts?.purchaseID ? reward.amount : 0,
                freeBoostersGranted: opts?.purchaseID ? 0 : reward.amount,
                totalBoostersGranted: reward.amount,
                feature: feature.feature,
                subFeature:
                  '$subFeature' in feature ? feature.$subFeature : undefined,
                boosterName: boosterNames[reward.subType],
                purchaseID: opts?.purchaseID,
              },
            },
          ]);
          break;
        case 'powerups':
          if (!powerups[reward.subType]) powerups[reward.subType] = 0;
          powerups[reward.subType] += reward.amount;
          sendAnalyticsEvents(api, [
            {
              eventType: 'PowerupGrant',
              eventProperties: {
                paidPowerupsGranted: opts?.purchaseID ? reward.amount : 0,
                freePowerupsGranted: opts?.purchaseID ? 0 : reward.amount,
                totalPowerupsGranted: reward.amount,
                feature: feature.feature,
                subFeature:
                  '$subFeature' in feature ? feature.$subFeature : undefined,
                powerupName: powerupNames[reward.subType],
                purchaseID: opts?.purchaseID,
              },
            },
          ]);

          break;
        case 'moves':
          inventory.extraMoves += reward.amount;
          break;
        case 'feature':
          grantFeatureReward(state, now, reward);
          break;
        case 'roomDecoration':
          grantRoomDecorationReward(state, now, reward);
          break;

        case 'cardPack':
          grantCardPackByTier(state, reward.tier, now);
          sendAnalyticsEvents(api, [
            {
              eventType: 'CardPackGrant',
              eventProperties: {
                feature: feature.feature,
                subFeature:
                  '$subFeature' in feature ? feature.$subFeature : undefined,
              },
            },
          ]);
          break;
        case 'card':
          cardsAddCard(state, { cardId: reward.cardId, now });
          break;
        default:
          assertNever(reward);
      }
    }
  }
}

function grantFeatureReward(
  state: MutableState,
  now: number,
  reward: FeatureReward,
) {
  switch (reward.subType) {
    case 'frenzy_objective':
      return;
    case 'race_points': {
      const { raceId, score, raceIndex } = state.match2.pendingRaceScore!;
      updateRaceScore(state, now, raceId, raceIndex, score);
      delete state.match2.pendingRaceScore;
      break;
    }
    case 'superLollipop':
      activateSuperLollipop(state);
      break;
  }
}

function grantRoomDecorationReward(
  state: MutableState,
  now: number,
  reward: {
    theme: DecorationThemeID;
    color: DecorationThemeVariant;
    tier: 1 | 2 | 3;
  },
) {
  const { theme, color, tier } = reward;
  for (const slot of decorationSlots) {
    const tierItem = tiersByTheme[theme][color][slot][tier];
    addDecorationItem(state, tierItem.item_id, tierItem.id, now);
  }
}

/*
 * Grant a timed unlimited reward or extend the duration of an existing one
 */
function grantMatch2TimedReward(
  state: MutableState,
  now: number,
  type: Match2TimedRewardType,
  duration: number,
  api: SyncActionAPI,
  feature: CurrencyGrantFeature,
  opts?: CurrencyGrantOpts,
) {
  const remaining = getMatch2TimedRewardRemainingTime(state, type, now);
  const timestamp = now + remaining + duration;
  const cooldownKey = getMatch2TimedRewardTimestampKey(type);

  // set the timed reward metadata
  if (!state.match2.ongoingTimedRewards) state.match2.ongoingTimedRewards = {};

  // auto-reset metadata if the remaining time has expired, or if undefined
  if (
    !state.match2.ongoingTimedRewards[cooldownKey] ||
    state.match2.ongoingTimedRewards[cooldownKey]?.expired ||
    remaining === 0
  ) {
    state.match2.ongoingTimedRewards[cooldownKey] = {
      startTime: now,
      levelStarted: getMatch2Level(state),
      usedItems: 0,
      expired: false,
    };
  }
  state.match2.ongoingTimedRewards[cooldownKey].usedItems += 1;

  // Schedule the expiration of the timed reward
  api.scheduledActions.schedule.expireTimedReward({
    notificationId: `expireTimedReward-${type}`,
    delayInMS: remaining + duration,
    args: {
      timedRewardType: type,
    },
  });

  // set the timestamp
  setMatch2Timestamp(state, cooldownKey, timestamp);

  // track the grant and consume events
  trackRewardAnalytics(state, type, duration, api, feature, opts);
}

/**
 * Tracks analytics events for granting and consuming a timed reward
 */
function trackRewardAnalytics(
  state: MutableState,
  rewardType: Match2TimedRewardType,
  durationMs: number,
  api: SyncActionAPI,
  feature: CurrencyGrantFeature,
  opts?: CurrencyGrantOpts,
): void {
  const durationMinutes = Math.floor(durationMs / duration({ minutes: 1 }));
  const durationLabel = `${durationMinutes}m` as '15m' | '30m' | '60m';

  const commonProperties = {
    feature: feature.feature,
    subFeature: '$subFeature' in feature ? feature.$subFeature : undefined,
    purchaseID: opts?.purchaseID,
    puzzleLevel: getMatch2Level(state),
    leaguePhase: hasJoinedLeague(state)
      ? getCurrentLeaguePhase(state)
      : undefined,
  };

  if (rewardType.includes('powerups')) {
    trackPowerupEvents(api, rewardType, durationLabel, commonProperties);
  } else if (rewardType.includes('boosters')) {
    trackBoosterEvents(api, rewardType, durationLabel, commonProperties);
  }
}

/**
 * Tracks powerup-specific analytics events
 */
function trackPowerupEvents(
  api: SyncActionAPI,
  rewardType: Match2TimedRewardType,
  durationLabel: '15m' | '30m' | '60m',
  commonProperties: {
    feature: string;
    subFeature?: string;
    purchaseID?: string;
    puzzleLevel: number;
    leaguePhase?: number;
  },
) {
  const powerupType = rewardType.split('-')[1] as keyof typeof powerupNames;
  const powerupBaseName = powerupNames[powerupType];
  const powerupName = `${powerupBaseName}_${durationLabel}` as const;

  sendAnalyticsEvents(api, [
    {
      eventType: 'PowerupGrant',
      eventProperties: {
        paidPowerupsGranted: commonProperties.purchaseID ? 1 : 0,
        freePowerupsGranted: commonProperties.purchaseID ? 0 : 1,
        totalPowerupsGranted: 1,
        powerupName,
        ...commonProperties,
      },
    },
    {
      eventType: 'PowerupConsume',
      eventProperties: {
        powerupName,
        ...commonProperties,
      },
    },
  ]);
}

/**
 * Tracks booster-specific analytics events
 */
function trackBoosterEvents(
  api: SyncActionAPI,
  rewardType: Match2TimedRewardType,
  durationLabel: '15m' | '30m' | '60m',
  commonProperties: {
    feature: string;
    subFeature?: string;
    purchaseID?: string;
    puzzleLevel: number;
    leaguePhase?: number;
  },
) {
  const boosterType = rewardType.split('-')[1] as keyof typeof boosterNames;
  const boosterBaseName = boosterNames[boosterType];
  const boosterName = `${boosterBaseName}_${durationLabel}` as const;

  sendAnalyticsEvents(api, [
    {
      eventType: 'BoosterGrant',
      eventProperties: {
        paidBoostersGranted: commonProperties.purchaseID ? 1 : 0,
        freeBoostersGranted: commonProperties.purchaseID ? 0 : 1,
        totalBoostersGranted: 1,
        boosterName,
        ...commonProperties,
      },
    },
    {
      eventType: 'BoosterConsume',
      eventProperties: {
        boosterName,
        ...commonProperties,
      },
    },
  ]);
}

/*
 * Set a timed unlimited reward - currently for admin action on CS Tool
 */
export function setMatch2TimedReward(
  state: MutableState,
  now: number,
  type: Match2TimedRewardType,
  duration: number,
): void {
  const timestamp = now + duration;
  const cooldownKey = getMatch2TimedRewardTimestampKey(type);

  // set the timed reward metadata
  if (!state.match2.ongoingTimedRewards) state.match2.ongoingTimedRewards = {};

  // auto-reset metadata if the remaining time has expired, or if undefined
  if (
    !state.match2.ongoingTimedRewards[cooldownKey] ||
    state.match2.ongoingTimedRewards[cooldownKey]?.expired
  ) {
    state.match2.ongoingTimedRewards[cooldownKey] = {
      startTime: now,
      levelStarted: getMatch2Level(state),
      usedItems: 0,
      expired: false,
    };
  }

  // set the timestamp
  setMatch2Timestamp(state, cooldownKey, timestamp);
}

/*
 * Set or update a timestamp by key
 */
export function setMatch2Timestamp(
  state: MutableState,
  key: string,
  timestamp: number,
): void {
  if (!state.match2.timestamps) state.match2.timestamps = {};
  state.match2.timestamps[key] = timestamp;
}

/**
 * Delete existing timestamp, if there are any
 */
export function clearMatch2Timestamp(state: MutableState, key: string): void {
  if (!state.match2.timestamps) return;
  delete state.match2.timestamps[key];
}

/**
 * End the current buru streak run and start a new one
 */
export function resetBuruStreak(state: MutableState): void {
  state.match2.buruStreak.value = 0;
  state.match2.buruStreak.id++;
}

/**
 * Set playing state to false and reset win streak if it was true before
 */
export function resetMatch2PlayingState(
  state: MutableState,
  now: number,
): void {
  // Reset match2 win streak if user closed the app during gameplay (playing = true)
  // and only if there are no valid save states
  if (state.match2.playing && !hasValidSaveState(state, now)) {
    state.match2.winStreak = 0;
    resetBuruStreak(state);
    // Only reset super lollipop streak if it was active (>= 10 wins),
    // matching the loss behavior in completeMatch2Puzzle
    if (isSuperLollipopActive(state)) {
      clearSuperLollipopWinStreak(state);
    }
  }
  state.match2.playing = false;
}
